/* ------------------------------------------------------------------------
  Copyright (c) 2016, VoiceVibes, Inc

  VoiceVibes, Inc - PROPRIETARY/CONFIDENTIAL
  All contents of this file are proprietary and confidential. No part of
  any of the contents of this file may be disclosed in any manner to
  a third party without the prior written consent of VoiceVibes, Inc.
  Unauthorized copying of this file, via any medium is strictly prohibited.
-----------------------------------------------------------------------------*/

<template>
  <section aria-label="Recording Comparisons">
    <h2
      id="score-name"
    >
      {{ score.name }}
    </h2>
    <table
      class="sr-only"
    >
      <caption>List of all scores for {{ score.name }}</caption>
      <thead>
        <tr>
          <th scope="col">
            Recording
          </th>
          <th scope="col">
            Score
          </th>
        </tr>
      </thead>
      <tbody v-for="(recording, index) in sortedRecordings">
        <tr :class="{ active: index % 2 == 0 }">
          <th scope="row">
            {{ recording.title }}
          </th>
          <td>{{ getScore(recording, score) }}</td>
        </tr>
      </tbody>
    </table>
    <div
      class="svg-container histchart-container"
      aria-hidden="true"
    >
      <svg
        viewBox="0 0 1100 700"
        class="histchart"
      >
        <g>
          <line
            fill="none"
            stroke="#333"
            stroke-width="1"
            x1="50"
            y1="350"
            x2="1050"
            y2="350"
          />
          <text
            v-for="(recording,index) in sortedRecordings"
            :key="recording.recordingId"
            fill="#F26724"
            class="text-link"
            :data-id="recording.recordingId"
            text-anchor="start"
            font-size="11"
            dy="16"
            :transform="`translate(${getX(index+.5)},360) rotate(45,0,0)`"
            @click="evaluate"
          >
            {{ recording.title }}
          </text>
        </g>
        <g>
          <line
            fill="none"
            stroke="#333"
            stroke-width="1"
            x1="50"
            y1="50"
            x2="50"
            y2="350"
          />
          <line
            v-for="n in 5"
            fill="none"
            stroke="#f2f2f2"
            stroke-width="1"
            stroke-dasharray="5"
            x1="50"
            :y1="350-n*60"
            x2="1050"
            :y2="350-n*60"
          />
          <text
            v-for="n in 6"
            fill="#333"
            x="45"
            :y="350-(n-1)*60"
            text-anchor="end"
            dy=".5ex"
            font-size="14"
          >
            {{ yAxis.steps[n-1] }}
          </text>
          <text
            fill="#333"
            text-anchor="middle"
            font-size="14"
            dy="14"
            transform="translate(0,200) rotate(-90,0,0)"
          >
            {{ score.unit }}
          </text>
        </g>
        <g>
          <rect
            v-if="series.idealZone"
            class="histchart-ideal"
            :x="series.idealZone.x"
            :width="series.idealZone.width"
            :y="series.idealZone.y"
            :height="series.idealZone.height"
          />
          <polyline
            class="histchart-line"
            :points="series.line"
          />
          <g
            v-for="p in series.points"
            v-if="p && 'x' in p && 'y' in p && !isNaN(p.x) && !isNaN(p.y)"
            class="histchart-col"
            :class="{ 'histchart-col-ideal': p.ideal }"
          >
            <rect
              :x="p.x-colWidth/2"
              y="50"
              :width="colWidth"
              height="300"
            />
            <text
              :x="p.x"
              :y="p.y + (p.y < 100 ? 30 : -30)"
              class="histchart-glow"
              text-anchor="middle"
              dy=".5ex"
            >
              {{ p.value }}
            </text>
            <text
              :x="p.x"
              :y="p.y + (p.y < 100 ? 30 : -30)"
              text-anchor="middle"
              dy=".5ex"
            >
              {{ p.value }}
            </text>
            <circle
              :cx="p.x"
              :cy="p.y"
              r="8"
            />
          </g>
        </g>
      </svg>
    </div>
  </section>
</template>

<style lang="less" scoped>
  @import '~bootstrap/less/variables';
  @import '../../less/bootstrap-vars';

  .svg-container {
    min-height: 300px;
  }
  #score-name {
    display: block;
    position: relative;
    top: 20px;
    font-size: 18px;
    margin: 0;
    padding: 0 15px;
  }
  .chart-legend {
    padding: 0 0 0 30px;
    margin: 0;
  }
  .text-link {
    cursor: pointer;
  }
  .algo-changed {
    font-size: 12pt !important;
    fill: #999999 !important;
  }
  .histchart-container {
    padding-top: 45.4545%; // 500/1100
  }
  .histchart {
    .histchart-line {
      fill: none;
      stroke: #999999;
      stroke-width: 3;
    }
    .histchart-col {
      circle {
        fill: @brand-primary;
        stroke: @body-bg;
        stroke-width: 2;
      }
      rect {
        fill: transparent;
      }
      text {
        fill: @brand-primary;
        font-size: 16px;
      }
      &:hover {
        circle {
          r: 12;
          stroke-width: 4;
        }
        rect {
          fill: rgba(129,38,102,.2);
        }
      }
      &.histchart-col-ideal {
        circle, text {
          fill: #812666;
        }
      }
    }
    .histchart-ideal {
      fill: #812666;
      stroke-width: 0;
      opacity: .1;
    }
  }
</style>

<script>
export default {
  props: {
    score: { default: { key: 'overall', name: 'Vocal Delivery', unit: 'score' } },
    recordings: {}
  },
  data() {
    return {};
  },
  computed: {
    colWidth() {
      return 1000 / this.recordings.length;
    },
    sortedRecordings() {
      return this._.sortBy(this.recordings, recording => recording.createdDate);
    },
    yAxis() {
      // The defaults for min and max are the values used for vibes

      const min = this._.min(this._.map(this.sortedRecordings, (r) => { return this.getRange(r, this.score.key)['min']; }));
      const max = this._.min(this._.map(this.sortedRecordings, (r) => { return this.getRange(r, this.score.key)['max']; }));
      return {
        min,
        max,
        steps: this._.map([0,0.2,0.4,0.6,0.8,1], s => Math.round((min+s*(max-min))*100)/100)
      };
    },
    series() {
      // For now, we assume that all recordings have the same ideals
      const lastRecIdx = this.sortedRecordings.length - 1;
      const range = this.getRange(this.sortedRecordings[lastRecIdx], this.score.key);
      const series = {
        points: this._.filter(this._.map(this.sortedRecordings, (recording, index) => {
          const value = this.getScore(recording, this.score);
          if (!value && value !== 0) {
            return null;
          }
          const x = this.getX(index + .5);
          const y = this.getY(value);
          const ideal = (typeof range.idealMin !== 'undefined' && typeof range.idealMax !== 'undefined')
              && (value >= range.idealMin && value <= range.idealMax);
          return { x, y, value, ideal };
        }), (obj) => obj)
      };
      if (typeof range.idealMin !== 'undefined' && typeof range.idealMax !== 'undefined') {
        const y = this.getY(range.idealMax);
        series.idealZone = {
          x: this.getX(0),
          width: series.points.length * this.colWidth,
          y,
          height: this.getY(range.idealMin) - y
        };
      }
      if (series.points.length > 1) {
        series.line = this._.map(series.points, (p) => {
          if (!p || isNaN(p.x) || isNaN(p.y)) return '';
          return `${p.x},${p.y}`;
        });
        series.line = series.line.join(' ');
      }
      return series;
    }
  },
  methods: {
    getRange(recording, metric) {
      if (recording && recording.results && recording.results.ideals[metric]) return recording.results.ideals[metric].range;
      if (metric === 'varietyVolume.inBand') {
        return recording.results.ideals.varietyVolume.range;
      } else if (recording && recording.results && recording.results.ideals && metric === 'varietyPace.value') {
        return recording.results.ideals.varietyPace.range;
      } else if (recording && recording.results && recording.results.ideals && metric === 'varietyPitch.value') {
        return recording.results.ideals.varietyPitch.range;
      }

      // For vibes:
      return {
        min: 0,
        max: 10
      };
    },
    getScore(recording, score) {
      if (score.key.indexOf('.') === -1) {
        return recording.results.scores[score.key];
      }
      const parts = score.key.split('.');
      return recording.results.scores[parts[0]][parts[1]];
    },
    evaluate(event) {
      const recordingId = event.target.dataset.id;
      this.$router.push({ name: 'evaluate-recording', params: { recordingId } });
    },
    getX(index) {
      return 50 + index * this.colWidth;
    },
    getY(value) {
      const scaled = (value - this.yAxis.min) / (this.yAxis.max - this.yAxis.min);
      return 350 - 300 * Math.min(1,Math.max(0,scaled));
    }
  }
};
</script>
